import { Injectable, inject } from '@angular/core';

import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { BehaviorSubject, Observable, Subject, of, timer } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  filter,
  finalize,
  first,
  map,
  shareReplay,
  take,
  tap
} from 'rxjs/operators';
import { LoginDataModel } from 'src/app/core/auth/models/login.model';
import { ApiLiv } from 'src/app/core/services/api/api-liv.service';
import { SegmentsStateService } from 'src/app/core/services/segments-state.service';

import { AuthStateService } from './auth-state.service';
import { SessionExpiryService } from './session-expiry.service';
import { UserProfileService } from './user-profile.service';

const PUBLIC_PROFILE_PATH_MARKERS = ['/auth', '/assistir-video'] as const;

export function isPublicProfileUrl(url: string): boolean {
  return PUBLIC_PROFILE_PATH_MARKERS.some((marker) => url.includes(marker));
}

/** What a sign-in tells listeners about itself. */
export type LoginCompleted = {
  /** No previous access was recorded for this account before this one. */
  isFirstLogin: boolean;
};

/**
 * The API builds the login response before recording the current access, so
 * `lastLogin` there is the *previous* one — `null` meaning the account had
 * never signed in.
 *
 * A missing field (an API that does not send it yet) counts as **not** a
 * first login on purpose: erring that way shows a prompt to someone it
 * shouldn't, while the opposite would silently disable every prompt that
 * depends on this and go unnoticed.
 */
function isFirstLogin(user: LoginDataModel): boolean {
  return user.lastLogin === null;
}

@Injectable({ providedIn: 'root' })
export class UserProfileBootstrapService {
  private readonly api = inject(ApiLiv);
  private readonly authStateService = inject(AuthStateService);
  private readonly sessionExpiry = inject(SessionExpiryService);
  private readonly segmentsStateService = inject(SegmentsStateService);
  private readonly i18n = inject(I18nService);
  private readonly userProfileService = inject(UserProfileService);

  private readonly MIN_LOADING_MS = 750;
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly bootstrapCompleteSubject = new BehaviorSubject<boolean>(
    false
  );

  private readonly loginCompletedSubject = new Subject<LoginCompleted>();

  private inFlight$: Observable<boolean> | undefined;

  readonly loading$ = this.loadingSubject.asObservable();
  readonly bootstrapComplete$ = this.bootstrapCompleteSubject.asObservable();

  /**
   * Emits once per **actual sign-in** (credentials or SaltaID), which both
   * land on `applyFromLoginResponse`. It never emits when a stored session is
   * merely restored on a reload or navigation — that path is `ensureLoaded`.
   *
   * This is the signal for anything that must interrupt the user right after
   * signing in and not on every page load: `isAuthenticated$` cannot express
   * that, since it is already `true` the moment the app boots with a session
   * in storage. Deliberately in memory only, so a refresh does not replay it.
   *
   * Carries `isFirstLogin`, which only the login response can answer.
   */
  readonly loginCompleted$ = this.loginCompletedSubject.asObservable();

  constructor() {
    this.authStateService.isAuthenticated$
      .pipe(filter((isAuthenticated) => !isAuthenticated))
      .subscribe(() => {
        this.resetSessionCache();
      });
  }

  applyFromLoginResponse(
    user: LoginDataModel,
    options?: { skipLocaleSync?: boolean }
  ): Observable<boolean> {
    if (!user?.token) {
      return of(false);
    }

    this.processUserPayload(user, { skipLocaleSync: true });
    this.loginCompletedSubject.next({ isFirstLogin: isFirstLogin(user) });

    if (options?.skipLocaleSync) {
      return of(false);
    }

    return this.applyLocaleInApp().pipe(map(() => false));
  }

  waitUntilBootstrapComplete(): Observable<void> {
    if (this.bootstrapCompleteSubject.value) {
      return of(undefined);
    }

    return this.bootstrapComplete$.pipe(
      filter((complete) => complete),
      first(),
      map((): void => undefined)
    );
  }

  ensureLoaded(url: string): Observable<boolean> {
    const earlyResult = this.resolveEnsureLoadedEarly(url);
    if (earlyResult) {
      return earlyResult;
    }

    const token = this.authStateService.sessionSnapshot?.token;
    if (!token) {
      return this.completeBootstrapImmediately();
    }

    const cachedResult = this.tryResolveCachedBootstrap(token);
    if (cachedResult) {
      return cachedResult;
    }

    if (this.inFlight$) {
      return this.inFlight$;
    }

    return this.startUserProfileFetch();
  }

  resetSessionCache(): void {
    this.inFlight$ = undefined;
    this.userProfileService.clearBootstrapState();
    this.markBootstrapPending();
  }

  private markBootstrapComplete(): void {
    this.bootstrapCompleteSubject.next(true);
  }

  private markBootstrapPending(): void {
    this.bootstrapCompleteSubject.next(false);
  }

  private resolveEnsureLoadedEarly(url: string): Observable<boolean> | null {
    if (!this.authStateService.isAuthenticated) {
      this.resetSessionCache();
      return this.completeBootstrapImmediately();
    }

    if (isPublicProfileUrl(url)) {
      return this.completeBootstrapImmediately();
    }

    return null;
  }

  private completeBootstrapImmediately(): Observable<boolean> {
    this.markBootstrapComplete();
    return of(true);
  }

  private tryResolveCachedBootstrap(token: string): Observable<boolean> | null {
    if (!this.userProfileService.resolveBootstrappedToken(token)) {
      return null;
    }

    this.syncLocaleAndAccess();
    return this.completeBootstrapImmediately();
  }

  private startUserProfileFetch(): Observable<boolean> {
    const startedAt = Date.now();
    this.markBootstrapPending();
    this.loadingSubject.next(true);

    let localeReloadScheduled = false;

    this.inFlight$ = this.api.get<LoginDataModel>('/Usuario').pipe(
      tap((user) => {
        localeReloadScheduled = this.processUserPayload(user);
      }),
      map(() => true),
      catchError(() => of(true)),
      defaultIfEmpty(true),
      finalize(() => {
        if (localeReloadScheduled) {
          return;
        }

        this.finalizeUserProfileFetch(startedAt);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.inFlight$;
  }

  private finalizeUserProfileFetch(startedAt: number): void {
    const elapsed = Date.now() - startedAt;
    const remainder = Math.max(0, this.MIN_LOADING_MS - elapsed);

    timer(remainder)
      .pipe(take(1))
      .subscribe(() => {
        this.loadingSubject.next(false);
        this.inFlight$ = undefined;
        this.markBootstrapComplete();
      });
  }

  private processUserPayload(
    user: LoginDataModel,
    options?: { skipLocaleSync?: boolean; navigationUrl?: string | null }
  ): boolean {
    const token = user.token?.trim();
    if (!token) {
      return false;
    }

    this.authStateService.saveSession({
      token: user.token,
      refreshToken: user.refreshToken,
      tokenPortfolio: this.authStateService.sessionSnapshot?.tokenPortfolio,
      loginDate: Date.now(),
      expirationDate: user.dataDeExpiracao
    });

    this.userProfileService.saveProfile({
      id: user.id,
      email: user.email,
      nome: user.nome,
      espacoSer: user.espacoSer,
      perfisAcesso: user.perfisAcesso,
      features: user.features ?? [],
      country: user.country
    });

    this.userProfileService.setHasAcceptedTerms(user.aceitouTermos);

    this.syncLocalizedSegments(user.segmentos_strapi);

    let localeReloadScheduled = false;

    if (options?.skipLocaleSync) {
      this.checkAccessExpiration();
    } else {
      localeReloadScheduled = this.syncLocaleAndAccess(options?.navigationUrl);
    }

    this.userProfileService.markBootstrapped(token, localeReloadScheduled);
    return localeReloadScheduled;
  }

  private syncLocaleAndAccess(navigationUrl?: string | null): boolean {
    const country = this.userProfileService.profileSnapshot?.country;
    if (!country) {
      this.checkAccessExpiration();
      return false;
    }

    const localeReloadScheduled = this.i18n.syncLanguageFromCountry(
      country,
      navigationUrl ? { navigationUrl } : undefined
    );

    this.checkAccessExpiration();
    return localeReloadScheduled;
  }

  private syncLocalizedSegments(
    segments: LoginDataModel['segmentos_strapi']
  ): void {
    if (!segments?.length) {
      return;
    }

    this.segmentsStateService.syncSegments(segments);
  }

  private applyLocaleInApp(): Observable<boolean> {
    const country = this.userProfileService.profileSnapshot?.country;
    const lang = country ? this.i18n.getLanguageFromCountry(country) : null;

    if (!lang || lang === this.i18n.currentLanguage()) {
      return of(true);
    }

    return this.i18n.setLanguage(lang).pipe(
      take(1),
      map(() => true),
      catchError(() => of(true))
    );
  }

  private checkAccessExpiration(): void {
    this.sessionExpiry.expireIfCurrentTokenExpired();
  }
}
